import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User,
  Mail,
  Hash,
  DollarSign,
  RefreshCw,
  AlertCircle,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const ContributionApprovalSection = ({ eventId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contributions, setContributions] = useState([]);
  const [allContributions, setAllContributions] = useState([]);
  const [event, setEvent] = useState(null);
  const [totalPending, setTotalPending] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    transactionId: '',
    minAmount: '',
    maxAmount: '',
    email: ''
  });

  useEffect(() => {
    fetchPendingContributions();
  }, [eventId]);

  useEffect(() => {
    applyFilters();
  }, [filters, allContributions]);

  const fetchPendingContributions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events/${eventId}/pending-contributions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllContributions(response.data.pending_contributions);
      setContributions(response.data.pending_contributions);
      setEvent(response.data.event);
      setTotalPending(response.data.total_pending);
      setTotalAmount(response.data.total_pending_amount);
    } catch (error) {
      console.error('Failed to fetch pending contributions:', error);
      toast.error('Failed to load pending contributions');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allContributions];

    // Date filter
    if (filters.dateFrom) {
      filtered = filtered.filter(c => {
        const contribDate = new Date(c.created_at);
        const fromDate = new Date(filters.dateFrom);
        return contribDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter(c => {
        const contribDate = new Date(c.created_at);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        return contribDate <= toDate;
      });
    }

    // Transaction ID filter
    if (filters.transactionId) {
      filtered = filtered.filter(c => 
        c.transaction_id?.toLowerCase().includes(filters.transactionId.toLowerCase())
      );
    }

    // Amount filter
    if (filters.minAmount) {
      filtered = filtered.filter(c => c.total_contribution_amount >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(c => c.total_contribution_amount <= parseFloat(filters.maxAmount));
    }

    // Email filter
    if (filters.email) {
      filtered = filtered.filter(c => 
        c.guest_email?.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    setContributions(filtered);
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      transactionId: '',
      minAmount: '',
      maxAmount: '',
      email: ''
    });
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(v => v !== '');
  };

  const handleVerify = async (rsvpId, status) => {
    try {
      await axios.put(
        `${API}/events/${eventId}/verify-contribution/${rsvpId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Contribution ${status === 'verified' ? 'approved' : 'rejected'}`);
      fetchPendingContributions();
    } catch (error) {
      toast.error('Failed to update contribution status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-contribo-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Card */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Approvals</p>
              <p className="text-3xl font-bold text-green-600">{totalPending}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Pending Amount</p>
              <p className="text-3xl font-bold text-green-600">₹{totalAmount.toLocaleString()}</p>
            </div>
            <div className="flex items-center justify-end">
              <Button
                onClick={fetchPendingContributions}
                variant="outline"
                className="bg-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900 mb-1">Contribution Approval</p>
            <p className="text-xs text-blue-700">
              Review and verify that you have received the contribution payments in your UPI account. 
              Check the transaction ID against your bank statement before approving.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <CardTitle className="text-lg">Filters</CardTitle>
              {hasActiveFilters() && (
                <Badge variant="secondary" className="ml-2">
                  Active
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'}
            </Button>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent>
            <div className="space-y-4">
              {/* Date Range */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    From Date
                  </label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    To Date
                  </label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Transaction ID */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Transaction ID
                </label>
                <Input
                  type="text"
                  placeholder="Search by transaction ID..."
                  value={filters.transactionId}
                  onChange={(e) => setFilters({ ...filters, transactionId: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Amount Range */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Min Amount (₹)
                  </label>
                  <Input
                    type="number"
                    placeholder="Min amount"
                    value={filters.minAmount}
                    onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Max Amount (₹)
                  </label>
                  <Input
                    type="number"
                    placeholder="Max amount"
                    value={filters.maxAmount}
                    onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Guest Email
                </label>
                <Input
                  type="text"
                  placeholder="Search by email..."
                  value={filters.email}
                  onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Filter Actions */}
              {hasActiveFilters() && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Clear All Filters
                  </Button>
                </div>
              )}

              {/* Filter Results Info */}
              <div className="bg-gray-50 rounded p-3 text-sm text-gray-600">
                Showing {contributions.length} of {allContributions.length} pending contributions
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pending Contributions List */}
      {contributions.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</p>
            <p className="text-gray-600">No pending contribution approvals at this time.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-green-600" />
              Pending Goal Contribution Approvals
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Contributions made by guests to your event goals (sent to your UPI account)
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {contributions.map((rsvp) => (
                <div key={rsvp.id} className="border-2 border-blue-200 rounded-lg p-3 md:p-4 bg-gradient-to-br from-white to-blue-50 hover:shadow-lg transition-shadow">
                  {/* Header Section - Guest Info */}
                  <div className="mb-4 pb-3 border-b-2 border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">GUEST NAME</p>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-600" />
                          <span className="font-bold text-gray-900 text-sm md:text-base">{rsvp.guest_name}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">EMAIL ID</p>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-blue-600" />
                          <span className="text-gray-700 text-xs md:text-sm break-all">{rsvp.guest_email}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contribution Details Section */}
                  <div className="mb-4">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-xs font-bold text-gray-700 mb-3 uppercase">Goal Contributions</p>
                      <div className="space-y-2">
                        {rsvp.contributions?.map((contrib, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-gray-500 mb-0.5">GOAL NAME</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {contrib.goal_title || contrib.custom_goal_title}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-semibold text-gray-500 mb-0.5">AMOUNT</p>
                              <p className="text-lg font-bold text-green-600">₹{contrib.amount.toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Total Amount */}
                      <div className="mt-3 pt-3 border-t-2 border-gray-300 flex justify-between items-center">
                        <p className="text-xs font-bold text-gray-700 uppercase">Total Amount Contributed</p>
                        <p className="text-2xl md:text-3xl font-bold text-green-600">
                          ₹{rsvp.total_contribution_amount?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Information Section */}
                  <div className="mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Transaction ID */}
                      <div className="bg-yellow-50 rounded-lg p-3 border-2 border-yellow-300">
                        <p className="text-xs font-bold text-yellow-900 mb-2 uppercase">Transaction ID</p>
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                          <p className="font-mono text-sm md:text-base font-bold text-yellow-900 break-all">
                            {rsvp.transaction_id || 'Not provided'}
                          </p>
                        </div>
                      </div>

                      {/* Transaction Date */}
                      <div className="bg-purple-50 rounded-lg p-3 border-2 border-purple-300">
                        <p className="text-xs font-bold text-purple-900 mb-2 uppercase">Transaction Date</p>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-purple-600 flex-shrink-0" />
                          <p className="text-sm md:text-base font-semibold text-purple-900">
                            {rsvp.transaction_date ? new Date(rsvp.transaction_date).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div className="mt-3 bg-blue-50 rounded-lg p-3 border-2 border-blue-300">
                      <p className="text-xs font-bold text-blue-900 mb-2 uppercase">Payment Status</p>
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                        <p className="text-sm md:text-base font-bold text-orange-600">
                          ⏳ Pending Verification
                        </p>
                      </div>
                      <p className="text-xs text-blue-700 mt-2">
                        Submitted: {new Date(rsvp.created_at).toLocaleString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col md:flex-row gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleVerify(rsvp.id, 'verified')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs md:text-sm py-2 md:py-3"
                    >
                      <CheckCircle className="h-4 w-4 mr-1 md:mr-2" />
                      ✓ Approve Payment
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleVerify(rsvp.id, 'rejected')}
                      variant="destructive"
                      className="flex-1 text-xs md:text-sm py-2 md:py-3"
                    >
                      <XCircle className="h-4 w-4 mr-1 md:mr-2" />
                      ✗ Reject Payment
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 mt-2 text-center italic">
                    Verify payment received in your UPI account before approving
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContributionApprovalSection;
